import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { type AddressAccountSnapshot, ApiError, type ExplorerTxRecord, api, isAbortError } from '@/shared/api/client'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { getAddressFilterLabel } from '@/shared/lib/bizTypes'
import { getMethodLabel } from '@/shared/lib/format'
import { truncateMiddle } from '@/shared/lib/format'
import { isAddress } from '@/shared/lib/validators'
import CopyButton from '@/shared/ui/CopyButton'
import DetailRow from '@/shared/ui/DetailRow'
import InfoCard from '@/shared/ui/InfoCard'
import ListCard from '@/shared/ui/ListCard'
import { ErrorView, Loading } from '@/shared/ui/StateView'
import BizTypeFilter from '@/widgets/address-detail/BizTypeFilter'
import CursorPagination from '@/widgets/pagination/CursorPagination'
import TxListSummary from '@/widgets/tx-table/TxListSummary'
import TxTable from '@/widgets/tx-table/TxTable'

const PAGE_SIZE = 20

const parseCursorParam = (raw: string | null): number => {
  if (!raw) return 0
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

const AddressDetails: React.FC = () => {
  const { address } = useParams<{ address: string }>()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentCursor = parseCursorParam(searchParams.get('cursor'))
  const bizType = searchParams.get('bizType')?.trim() || undefined
  const [cursorHistory, setCursorHistory] = useState<number[]>([])
  const [transactions, setTransactions] = useState<ExplorerTxRecord[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState(0)
  const [account, setAccount] = useState<AddressAccountSnapshot | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [listLoading, setListLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [listError, setListError] = useState<string | null>(null)
  const [retryToken, setRetryToken] = useState(0)
  const hasLoadedOnceRef = useRef(false)
  const prevAddressRef = useRef(address)
  const prevBizTypeRef = useRef(bizType)

  const hasCursorParam = searchParams.has('cursor')

  useEffect(() => {
    hasLoadedOnceRef.current = false
    setInitialLoading(true)
    setListLoading(false)
    setError(null)
    setListError(null)
    setCursorHistory([])
    if (hasCursorParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('cursor')
          return next
        },
        { replace: true },
      )
    }
  }, [address, hasCursorParam, setSearchParams])

  const handleNext = useCallback(() => {
    if (!hasMore) return
    setCursorHistory((prev) => [...prev, currentCursor])
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('cursor', String(nextCursor))
        return next
      },
      { replace: true },
    )
  }, [currentCursor, hasMore, nextCursor, setSearchParams])

  const handlePrev = useCallback(() => {
    const prevCursor = cursorHistory.at(-1)
    if (prevCursor === undefined) return
    setCursorHistory((prev) => prev.slice(0, -1))
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (prevCursor <= 0) {
          next.delete('cursor')
        } else {
          next.set('cursor', String(prevCursor))
        }
        return next
      },
      { replace: true },
    )
  }, [cursorHistory, setSearchParams])

  const handleBizTypeChange = useCallback(
    (nextBizType: string | undefined) => {
      setCursorHistory([])
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('cursor')
          if (nextBizType) {
            next.set('bizType', nextBizType)
          } else {
            next.delete('bizType')
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  useEffect(() => {
    if (!address) return
    if (!isAddress(address)) {
      navigate('/404', { replace: true })
      return
    }

    // Track previous address/bizType to detect resets (must be inside effect, not render)
    const addressChanged = prevAddressRef.current !== address
    const bizTypeChanged = prevBizTypeRef.current !== bizType
    prevAddressRef.current = address
    prevBizTypeRef.current = bizType
    const effectiveCursor = addressChanged || bizTypeChanged ? 0 : currentCursor

    const ctrl = new AbortController()
    let cancelled = false
    const isInitial = !hasLoadedOnceRef.current
    if (isInitial) {
      setInitialLoading(true)
    } else {
      setListLoading(true)
    }
    if (isInitial) {
      setError(null)
    } else {
      setListError(null)
    }

    api
      .getAddressCursor(address, effectiveCursor, PAGE_SIZE, bizType, { signal: ctrl.signal })
      .then((data) => {
        if (cancelled) return
        setTransactions(data.list)
        setHasMore(data.hasMore)
        setNextCursor(data.nextCursor)
        setAccount(data.account ?? null)
        hasLoadedOnceRef.current = true
      })
      .catch((err: unknown) => {
        if (cancelled || isAbortError(err)) return
        if (err instanceof ApiError && err.isNotFound) {
          navigate('/404', { replace: true })
          return
        }
        const message = err instanceof Error ? err.message : 'Failed to load address details'
        if (isInitial) {
          setError(message)
        } else {
          setListError(message)
        }
      })
      .finally(() => {
        if (cancelled) return
        if (isInitial) {
          setInitialLoading(false)
        } else {
          setListLoading(false)
        }
      })

    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [address, currentCursor, bizType, navigate, retryToken])

  useEffect(() => {
    setCursorHistory([])
    if (hasCursorParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('cursor')
          return next
        },
        { replace: true },
      )
    }
  }, [bizType, hasCursorParam, setSearchParams])

  if (initialLoading) return <Loading />
  if (error) return <ErrorView message={error} onRetry={() => setRetryToken((t) => t + 1)} />

  const equity = account?.equity?.trim() ?? ''
  const currency = account?.currency?.trim() || 'USDC'
  const hasBalance = equity.length > 0
  const hasPrev = cursorHistory.length > 0
  const filterLabel = bizType ? (getAddressFilterLabel(bizType) ?? getMethodLabel(bizType)) : undefined

  const infoCardPadding: React.CSSProperties = {
    padding: isMobile ? 'var(--space-14)' : 'var(--space-3xl)',
    marginBottom: 'var(--space-2xl)',
  }

  return (
    <div style={{ width: '100%' }}>
      <h2
        style={{
          marginBottom: isMobile ? 'var(--space-lg)' : 'var(--page-heading-margin-bottom-desktop)',
          fontWeight: 600,
          letterSpacing: 'var(--tracking-title)',
          fontSize: isMobile ? 'var(--font-size-h3)' : 'var(--font-size-h2)',
        }}
      >
        Address Details
      </h2>

      <InfoCard style={infoCardPadding}>
        <DetailRow label="Address">
          {isMobile ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-xs)',
                overflow: 'hidden',
                justifyContent: 'flex-end',
              }}
            >
              <span
                style={{
                  color: 'var(--app-text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {truncateMiddle(address)}
              </span>
              <CopyButton text={address ?? ''} label="Copy address" />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', fontFamily: 'monospace' }}>
              <span style={{ wordBreak: 'break-all', marginRight: 'var(--space-md)' }}>{address}</span>
              <CopyButton text={address ?? ''} label="Copy address" />
            </div>
          )}
        </DetailRow>
        <DetailRow label="Balance" hideDivider>
          <span
            style={{
              color: 'var(--app-text-primary)',
              fontSize: isMobile ? 'var(--font-size-base)' : 'var(--font-size-h3)',
              fontWeight: 'bold',
              fontFamily: 'monospace',
            }}
          >
            {hasBalance ? `${equity} ${currency}` : '—'}
          </span>
        </DetailRow>
      </InfoCard>

      <ListCard>
        <BizTypeFilter value={bizType} onChange={handleBizTypeChange} />
        {listLoading ? (
          <Loading padding="var(--space-4xl)" />
        ) : listError ? (
          <ErrorView message={listError} onRetry={() => setRetryToken((t) => t + 1)} />
        ) : (
          <>
            <TxListSummary count={transactions.length} hasMore={hasMore} filterLabel={filterLabel} />
            <TxTable
              rows={transactions}
              columns={['hash', 'action', 'block', 'time', 'address', 'amount']}
              contextAddress={address}
              emptyText="No records."
            />
            <CursorPagination hasPrev={hasPrev} hasNext={hasMore} onPrev={handlePrev} onNext={handleNext} />
          </>
        )}
      </ListCard>
    </div>
  )
}

export default AddressDetails
